import type { ProjectAction } from "../lib/brain-api";
import type { PillTone } from "./StatusPill";

/** Demo-faithful status derivation for the action register. */
export function deriveActionStatus(a: ProjectAction): { label: string; tone: PillTone } {
  if (a.status === "dismissed") return { label: "Dismissed", tone: "neutral" };
  if (a.status === "done") return { label: "Done", tone: "success" };
  if (a.clientStatus) return { label: String(a.clientStatus), tone: "warning" };
  if (a.jiraIssueKey) return { label: "Raised", tone: "teal" };
  return { label: "Not raised", tone: "neutral" };
}

/**
 * InlineSelect options for a Jira-backed register row: current status as the
 * empty value, then each available transition (by id → destination status).
 * `transitions === null` means still loading after open.
 */
export function jiraTransitionOptions(
  currentStatus: string,
  transitions: Array<{ id: string; toStatus: string }> | null,
): { value: string; label: string; disabled?: boolean }[] {
  const current = currentStatus.trim() || "—";
  const opts: { value: string; label: string; disabled?: boolean }[] = [
    { value: "", label: current, disabled: true },
  ];
  if (transitions === null) {
    opts.push({ value: "__loading__", label: "Loading…", disabled: true });
    return opts;
  }
  if (transitions.length === 0) {
    opts.push({ value: "__none__", label: "No transitions", disabled: true });
    return opts;
  }
  for (const t of transitions) {
    opts.push({ value: t.id, label: t.toStatus });
  }
  return opts;
}

export function priorityTone(priority?: string): PillTone {
  const p = (priority ?? "").toUpperCase();
  if (p === "P1") return "danger";
  if (p === "P2") return "warning";
  return "neutral";
}

export const JIRA_BROWSE = "https://aeronetnz.atlassian.net/browse";

/** Accept a bare key or a pasted browse URL → ANX-123. */
export function parseJiraKey(raw: string): string {
  const t = raw.trim();
  if (!t) return "";
  const fromUrl = t.match(/\/browse\/([A-Za-z][A-Za-z0-9]+-\d+)/);
  if (fromUrl?.[1]) return fromUrl[1].toUpperCase();
  const bare = t.match(/([A-Za-z][A-Za-z0-9]+-\d+)/);
  if (bare?.[1]) return bare[1].toUpperCase();
  return t.toUpperCase();
}

/** Next customerRef like #071 from existing refs + count. */
export function nextCustomerRef(actions: ProjectAction[]): string {
  let max = 0;
  for (const a of actions) {
    const m = String(a.customerRef ?? "").match(/#?(\d+)/);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `#${String(max + 1).padStart(3, "0")}`;
}

export function sortActionsByCreated(rows: ProjectAction[]): ProjectAction[] {
  return [...rows].sort((a, b) => {
    const ta = a.createdAt ?? 0;
    const tb = b.createdAt ?? 0;
    if (ta !== tb) return ta - tb;
    return a.id.localeCompare(b.id);
  });
}

export function groupActionsByWorkStream(
  actions: ProjectAction[],
  order: string[] = [],
): { stream: string; rows: ProjectAction[] }[] {
  // Caller filters Open/Done/All (and may include dismissed). Do not drop here.
  const map = new Map<string, ProjectAction[]>();
  for (const a of actions) {
    const key = a.workStream?.trim() || "(ungrouped)";
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(a);
  }
  const seen = new Set<string>();
  const out: { stream: string; rows: ProjectAction[] }[] = [];
  for (const ws of order) {
    if (map.has(ws)) {
      out.push({ stream: ws, rows: sortActionsByCreated(map.get(ws)!) });
      seen.add(ws);
    }
  }
  for (const [stream, rows] of map) {
    if (stream === "(ungrouped)") continue;
    if (!seen.has(stream)) out.push({ stream, rows: sortActionsByCreated(rows) });
  }
  if (map.has("(ungrouped)")) out.push({ stream: "(ungrouped)", rows: sortActionsByCreated(map.get("(ungrouped)")!) });
  return out;
}
